<template>
  <div class="wrapper-style">
    <template v-if="isImageAttachment">
      <a :href="value" @click.prevent>
        <img :src="value" alt="Form submission attachment" @click="attachmentClicked" />
      </a>
      <vs-text>{{ fileName }}</vs-text>
    </template>
    <template v-else>
      <a :href="value" @click.prevent>
        <v-row dense @click="attachmentClicked">
          <v-col class="shrink">
            <v-icon>insert_drive_file</v-icon>
          </v-col>
          <v-col align-self="start">
            <vs-text>{{ fileName }}</vs-text>
          </v-col>
        </v-row>
      </a>
    </template>
  </div>
</template>

<script setup lang="ts">
import { last } from 'lodash'
import { JPEG, JPG, PNG } from '@/utils/constants'
import { parseDataUrlMimeType } from '@/utils/data-url'
import { computed } from 'vue'
import MIMEType from 'whatwg-mimetype'
import VsText from '@/components/vision/VsText.vue'
import { useActions } from 'vuex-composition-helpers'

interface Props {
  value: string
}

const props = defineProps<Props>()

const { fetchFormSubmission } = useActions('formSubmissions', ['fetchFormSubmission'])

const dataUrlProps = computed<null | MIMEType>(() => {
  return parseDataUrlMimeType(props.value)
})

const isImageAttachment = computed<boolean>(() => {
  return (
    dataUrlProps.value?.type === 'image' ||
    [PNG.extension, JPEG.extension, JPG.extension].some(
      (ext) => urlFileExtension.value?.toLowerCase() === ext,
    )
  )
})

const urlFileExtension = computed<string | undefined>(() => {
  return new URL(props.value).pathname.split('.').pop()
})

const fileName = computed<string>(() => {
  if (dataUrlProps.value) {
    return decodeURIComponent(dataUrlProps.value.parameters.get('download') ?? 'unknown')
  }

  const rawFileName = last(new URL(props.value).pathname.split('/'))
  return decodeURIComponent(rawFileName ?? '')
})

function attachmentClicked() {
  if (dataUrlProps.value) {
    // Old style, stored in DB as data:url
    const image = new Image()
    image.src = props.value

    const newWindow = window.open('', '_blank')
    newWindow?.document.write(image.outerHTML)
  } else {
    if (isTokenValid()) {
      window.open(props.value, '_blank')
      return
    }

    const submissionId = getSubmissionId()
    if (submissionId == null) {
      return
    }
    fetchFormSubmission(submissionId)
  }
}

function getSubmissionId(): string | null {
  const urlSegments = props.value.split('/')
  const submissionIndex = urlSegments.length - 2
  if (submissionIndex < 0) {
    return null
  }

  return urlSegments[submissionIndex]
}

function isTokenValid() {
  const urlParams = new URLSearchParams(props.value)
  const expiry = urlParams.get('se')

  if (expiry) {
    const date = Date.parse(expiry)
    const now = Date.parse(new Date().toISOString())
    return date > now
  }

  // Could not get the expiry from the url. Assume still valid to prevent any infinite loops
  return true
}
</script>

<style scoped>
.wrapper-style {
  margin-top: 4px;
  border-radius: 12px;
  padding: 8px;
  border: 2px solid var(--color-grey--lighter);
}

img {
  border-radius: 12px;

  width: 100%;
  height: 100%;
}

a {
  text-decoration: none;
}
</style>
